import React, { useEffect, useState } from "react";
import * as api from "../../api/index";
import { jwtDecode } from "jwt-decode";
import { useNavigate } from "react-router-dom";
import { IoMdArrowBack } from "react-icons/io";
import { 
  FiPackage, FiMapPin, FiCalendar, FiChevronRight, 
  FiClock, FiCheckCircle, FiTruck, FiAlertCircle 
} from "react-icons/fi";

const TrackOrder = () => {
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const token = localStorage.getItem("token");
  const decoded = token ? jwtDecode(token) : {};
  const userId = decoded.id || decoded._id;

  useEffect(() => {
    const fetchOrders = async () => {
      if (!userId) {
        setError("User not logged in.");
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError("");
        const res = await api.Gettrackorders(userId, page, 20);

        if (res.data.orders) {
          setOrders(res.data.orders);
          setTotalPages(res.data.totalPages || 1);
        } else {
          setOrders([]);
          setTotalPages(1);
          setError("No orders available.");
        }
      } catch (err) {
        setError("Failed to fetch orders.");
      } finally {
        setLoading(false);
      }
    };

    fetchOrders();
  }, [userId, page]);

  // Loading State
  if (loading) return (
    <div className="flex flex-col items-center justify-center min-h-screen bg-gray-50">
      <div className="w-12 h-12 border-4 border-orange-200 border-t-orange-600 rounded-full animate-spin"></div>
      <p className="mt-4 font-black text-gray-400 uppercase tracking-widest text-[10px]">Updating Status...</p>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50 pb-24">
      {/* 🔝 Sticky Header */}
      <div className="sticky top-0 z-20 bg-white/80 backdrop-blur-md border-b px-4 py-4 flex justify-between items-center">
        <button onClick={() => navigate(-1)} className="p-2 hover:bg-gray-100 rounded-full transition flex items-center gap-1 text-gray-700">
          <IoMdArrowBack size={24} />
          <span className="hidden sm:inline font-bold">Back</span>
        </button>
        <h1 className="text-lg font-black text-gray-900 tracking-tight">Order Tracking</h1>
        <div className="w-10"></div> {/* Spacer for symmetry */}
      </div>

      <div className="max-w-xl mx-auto p-4 sm:p-6">
        {error ? (
          <div className="bg-white p-10 rounded-[2.5rem] text-center shadow-xl shadow-gray-200/50 border border-gray-100">
             <FiAlertCircle className="mx-auto text-gray-300 mb-4" size={48}/>
             <p className="text-gray-500 font-bold">{error}</p>
          </div>
        ) : (
          <div className="space-y-4">
            {orders.map((order) => (
              <div
                key={order._id}
                className="bg-white shadow-xl shadow-gray-200/40 rounded-[2rem] p-6 border border-gray-50 hover:border-orange-200 transition-all active:scale-[0.98]"
              >
                <div className="flex justify-between items-start mb-4">
                  <div className="flex items-center gap-3">
                    <div className="bg-orange-50 p-3 rounded-2xl text-orange-600">
                      <FiPackage size={24} />
                    </div>
                    <div>
                      <h3 className="font-black text-gray-900 leading-none">{order.item}</h3>
                      <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest mt-1">
                        Ref: #{order._id.slice(-6).toUpperCase()}
                      </p>
                    </div>
                  </div>
                  <span
                    className={`px-3 py-1.5 rounded-xl text-[10px] font-black uppercase tracking-wider border ${
                      order.status === "completed"
                        ? "bg-green-50 text-green-600 border-green-100"
                        : order.status === "pending"
                        ? "bg-yellow-50 text-yellow-600 border-yellow-100"
                        : "bg-blue-50 text-blue-600 border-blue-100"
                    }`}
                  >
                    {order.status}
                  </span>
                </div>

                <div className="space-y-3 mb-6">
                  <div className="flex items-start gap-3">
                    <FiMapPin className="text-gray-400 mt-1" size={14} />
                    <p className="text-xs text-gray-600 font-bold leading-relaxed">
                      {order.deliveryAddress}
                    </p>
                  </div>
                  <div className="flex items-center gap-3">
                    <FiCalendar className="text-gray-400" size={14} />
                    <p className="text-xs text-gray-400 font-bold">
                      {new Date(order.createdAt).toLocaleDateString(undefined, { dateStyle: 'long' })}
                    </p>
                  </div>
                </div>

                <div className="flex items-center justify-between pt-4 border-t border-gray-50">
                  <div>
                    <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Total Amount</p>
                    <p className="text-lg font-black text-gray-900">₦{order.amount?.toLocaleString()}</p>
                  </div>
                  <button
                    onClick={() => navigate(`/track-order/${order._id}`)}
                    className="flex items-center gap-2 bg-orange-600 text-white px-5 py-3 rounded-2xl font-black text-xs shadow-lg shadow-orange-100 hover:bg-orange-700 transition-all"
                  >
                    Track Stage <FiChevronRight />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* 📟 Pagination */}
        {totalPages > 1 && (
          <div className="flex justify-between items-center mt-10 px-2">
            <button
              disabled={page === 1}
              onClick={() => setPage((p) => Math.max(p - 1, 1))}
              className="p-3 bg-white shadow-md rounded-2xl disabled:opacity-30 transition-all active:scale-90"
            >
              <IoMdArrowBack size={20} className="text-gray-700" />
            </button>
            
            <div className="text-center">
                <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Progress</p>
                <span className="text-xs font-black text-gray-900">
                  {page} <span className="text-gray-300 mx-1">/</span> {totalPages}
                </span>
            </div>

            <button
              disabled={page === totalPages}
              onClick={() => setPage((p) => Math.min(p + 1, totalPages))}
              className="p-3 bg-white shadow-md rounded-2xl disabled:opacity-30 transition-all active:scale-90"
            >
              <FiChevronRight size={20} className="text-gray-700" strokeWidth={3} />
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default TrackOrder;